import { ThemeProvider } from '@mui/material/styles';
import { RouterProvider } from '@tanstack/react-router';
import { initSentry } from '@vestigas/sentry';
import { AuthProvider } from 'oidc-react';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { Provider as ReduxProvider } from 'react-redux';

import { queryClient } from '~/data/queryClient';

import authService from '~/services/auth.service';

// Initialize i18next
import '~/utils/i18n';

import '@vestigas/ui/index.css';
import { HotkeysProvider } from '@vestigas/utils';

import '~/ui/styles/index.css';
import '~/ui/styles/legacy.css';
import { Spinner } from '@vestigas/ui/molecules';
import { theme } from '~/ui/styles';

import { Config } from './Config';
import { AppInitializer } from './modules/app-initializer';
import { tanstackRouter } from './modules/router';
import store from './redux/store';
import { initMuiPro } from './services/thirdParty.service';

initMuiPro();

const router = tanstackRouter({ auth: authService, queryClient });

function RouterProviderWithContext() {
  return (
    <RouterProvider
      router={router}
      context={{
        auth: authService,
        queryClient,
      }}
    />
  );
}

if (Config.environment !== 'development') {
  initSentry({
    dsn: Config.SentryDSN,
    environment: Config.environment,
    release: `admin-portal@${Config.version}`,
    router,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/app\.vestigas\.com\/api/,
    ],
  });
}

export const App = () => {
  // Before the AuthProvider can auto-redirect to Keycloak, try to silently
  // restore the session from the stored refresh token. Without this, every
  // browser restart (sessionStorage wiped) forces a visible re-login even
  // though the refresh token is still valid.
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  useEffect(() => {
    void authService.restoreSession().finally(() => {
      setIsRestoringSession(false);
    });
  }, []);

  if (isRestoringSession) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <ReduxProvider store={store}>
      <ThemeProvider theme={theme}>
        <AuthProvider {...authService.getOidcConfig()}>
          <HotkeysProvider>
            <AppInitializer>
              <RouterProviderWithContext />
            </AppInitializer>
            <Toaster
              containerStyle={{ zIndex: 1400 }}
              position="top-right"
              toastOptions={{
                className: '',
                style: {
                  borderRadius: '5px',
                  maxWidth: '60vw',
                  padding: '5px 0px 5px 5px',
                  zIndex: 1400, // = MUI theme zIndex.snackbar
                },
              }}
            />
          </HotkeysProvider>
        </AuthProvider>
      </ThemeProvider>
    </ReduxProvider>
  );
};
